'use client';

import Loading from '@/app/components/Loading';
import StepsChart from '@/app/components/StepsChart';
import type { Workout } from '@trainme/db/client';
import {
  normalizeWorkoutSteps,
  parseWorkoutData,
  workoutStepsExampleLines,
} from '@trainme/api/client';
import { useRef, useMemo } from 'react';
import { useSettingsStore } from '@/app/stores/settingsStore';
import StrengthWorkoutChart from './StrengthWorkoutChart';

type Props = {
  workout: Workout | undefined;
  createKey: string;
  bottomMarginOffset?: number;
  compact?: boolean;
  workoutStepsOverride?: string[];
};

export const resolveStrengthWorkoutSteps = (
  workout: Workout | undefined,
  workoutStepsOverride?: string[]
) => {
  if (workoutStepsOverride) {
    return workoutStepsOverride;
  }
  return Array.isArray(workout?.steps) ? workout.steps.map(String) : [];
};

export function WorkoutChart({
  workout,
  createKey = '',
  bottomMarginOffset = 0,
  compact = false,
  workoutStepsOverride,
}: Props) {
  const chartRef = useRef<HTMLDivElement>(null);
  const getSportsSpeeds = useSettingsStore((state) => state.getSportsSpeeds);
  const isStrengthWorkout = workout?.sportType === 'WeightTraining';
  const chartData = useMemo(() => {
    const normalizedSteps = normalizeWorkoutSteps(workout?.steps);
    const steps =
      normalizedSteps.length > 0 ? normalizedSteps : workoutStepsExampleLines;
    const currentSportType = workout?.sportType || 'Run';
    const currentSportsSpeeds = getSportsSpeeds();
    const workoutData = parseWorkoutData(
      steps,
      currentSportType,
      currentSportsSpeeds
    );
    return workoutData.map((item) => ({
      key: `workout-${workout?.id}-${createKey}`,
      time: item.time,
      zone: item.zone,
    }));
  }, [workout, createKey, getSportsSpeeds]);

  if (isStrengthWorkout) {
    const strengthWorkoutSteps = resolveStrengthWorkoutSteps(
      workout,
      workoutStepsOverride
    );
    return (
      <StrengthWorkoutChart
        workoutSteps={strengthWorkoutSteps}
        compact={compact}
      />
    );
  }

  if (!chartData.length) return <Loading />;

  return (
    <div className='w-full h-full'>
      <StepsChart
        chartData={chartData}
        chartRef={chartRef}
        bottomMarginOffset={bottomMarginOffset}
      />
    </div>
  );
}
